import * as React from 'react';

import { cn } from '../../lib/utils';
import { Input } from './input';

/**
 * NumberInput — a plain numeric field that holds a number, not a string.
 *
 * Avoids `<input type="number">`: its spinners are browser furniture, it lets
 * "1e5" through, and a mouse wheel over a focused field silently changes the
 * value. This accepts digits only, groups thousands while not focused so a
 * six-figure odometer stays readable, and reports `null` when cleared.
 */
const NumberInput = React.forwardRef(
  ({ value, onChange, suffix, max, allowDecimal = false, className, ...props }, ref) => {
    const [draft, setDraft] = React.useState('');
    const [focused, setFocused] = React.useState(false);

    const display = React.useMemo(() => {
      if (focused) return draft;
      if (value === null || value === undefined || value === '') return '';
      return Number(value).toLocaleString('en-NZ');
    }, [focused, draft, value]);

    const parse = (raw) => {
      const cleaned = allowDecimal ? raw.replace(/[^\d.]/g, '') : raw.replace(/\D/g, '');
      if (cleaned === '') return null;
      const parsed = Number(cleaned);
      if (!Number.isFinite(parsed)) return null;
      return max !== undefined && parsed > max ? max : parsed;
    };

    return (
      <div className="relative">
        <Input
          ref={ref}
          type="text"
          inputMode={allowDecimal ? 'decimal' : 'numeric'}
          value={display}
          className={cn('tabular-nums', suffix && 'pr-10', className)}
          onFocus={(e) => {
            setFocused(true);
            setDraft(value === null || value === undefined ? '' : String(value));
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onChange?.(parse(draft));
            props.onBlur?.(e);
          }}
          onChange={(e) => {
            const raw = allowDecimal ? e.target.value.replace(/[^\d.]/g, '') : e.target.value.replace(/\D/g, '');
            setDraft(raw);
            onChange?.(parse(raw));
          }}
          {...props}
        />
        {suffix && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            {suffix}
          </span>
        )}
      </div>
    );
  }
);

NumberInput.displayName = 'NumberInput';

export { NumberInput };
